import type { LeadStatus } from '../enums';
import {
  DealType,
  InteractionResult,
  InteractionStatus,
  InteractionType,
  PipelineStage,
} from '../enums';
import { isNullDate } from './dates';
import { isAnnulledDeal, isValidVisit, realInteractions } from './stages';
import type { FunnelDeal, FunnelInteraction, FunnelLead } from './types';

export interface PipelineFacts {
  hasDesisted: boolean;
  status: LeadStatus;
  hasSale: boolean;
  hasActiveSeparation: boolean;
  hasQuote: boolean;
  hasValidVisit: boolean;
  hasAppointment: boolean;
  hasEffectiveContact: boolean;
}

/** Nombres de las etapas del kanban tal como los ve el usuario (web y exportaciones). */
export const PIPELINE_STAGE_LABELS: Record<PipelineStage, string> = {
  NUEVO: 'Nuevo',
  CONTACTADO: 'Contactado',
  CITA: 'Cita generada',
  VISITA: 'Visita',
  PROFORMA: 'Proforma',
  SEPARACION: 'Separación',
  VENTA: 'Venta',
  PERDIDO: 'Perdido',
};

/** Etapa del kanban a partir de hechos: la más avanzada gana; desistido = PERDIDO. */
export function derivePipelineStage(f: PipelineFacts): PipelineStage {
  if (f.hasDesisted) return PipelineStage.PERDIDO;
  if (f.hasSale) return PipelineStage.VENTA;
  if (f.hasActiveSeparation) return PipelineStage.SEPARACION;
  if (f.hasQuote) return PipelineStage.PROFORMA;
  if (f.hasValidVisit) return PipelineStage.VISITA;
  if (f.hasAppointment) return PipelineStage.CITA;
  if (f.hasEffectiveContact) return PipelineStage.CONTACTADO;
  return PipelineStage.NUEVO;
}

/**
 * Hechos del lead para derivar la etapa. `quoteCount` = proformas no anuladas (vigentes, expiradas o convertidas).
 * Una venta o separación devuelta / anulada deja de sostener su etapa: así el kanban "retrocede" solo.
 */
export function pipelineFacts(
  lead: Pick<FunnelLead, 'hasDesisted' | 'status'>,
  allInteractions: readonly FunnelInteraction[],
  deals: readonly FunnelDeal[],
  quoteCount: number,
): PipelineFacts {
  const live = deals.filter((d) => !isAnnulledDeal(d));
  // Las simulaciones del bot no son hechos: no mueven la etapa.
  const interactions = realInteractions(allInteractions);
  return {
    hasDesisted: lead.hasDesisted,
    status: lead.status,
    hasSale: live.some(
      (d) => d.type === DealType.VENTA && !isNullDate(d.finishedAt) && isNullDate(d.refundedAt),
    ),
    hasActiveSeparation: live.some(
      (d) => d.type === DealType.SEPARACION && !isNullDate(d.startedAt) && isNullDate(d.refundedAt),
    ),
    hasQuote:
      quoteCount > 0 ||
      interactions.some(
        (i) => i.type === InteractionType.PROFORMA && i.status === InteractionStatus.COMPLETADA,
      ),
    hasValidVisit: interactions.some(isValidVisit),
    hasAppointment: interactions.some(
      (i) => i.type === InteractionType.SE_CITO && i.status !== InteractionStatus.CANCELADA,
    ),
    hasEffectiveContact: interactions.some(
      (i) => i.status === InteractionStatus.COMPLETADA && i.result === InteractionResult.EFECTIVO,
    ),
  };
}

/**
 * Etapas sostenidas por un proceso vivo (separación o venta activa). Un lead así no se desiste directamente:
 * primero se devuelve o anula el proceso («Devolver y desistir»), porque la etapa se deriva de los hechos y una
 * separación viva con el lead perdido dejaría la unidad bloqueada y la caída sin contar (Lote 5 · H1).
 */
export function stageHasLiveDeal(stage: PipelineStage): boolean {
  return stage === PipelineStage.SEPARACION || stage === PipelineStage.VENTA;
}

/**
 * Retroceder en el kanban = anular el hecho que sostiene la etapa actual (ADR-007 / ADR-011).
 * CITA: cancelar la cita pendiente. PROFORMA: anular la(s) proforma(s). SEPARACION / VENTA: devolución o anulación
 * del proceso. Contactos y visitas son hechos ocurridos y no se deshacen desde el kanban.
 */
export type StageUndoKind = 'CANCEL_APPOINTMENT' | 'ANNUL_QUOTE' | 'REFUND_DEAL';

export function stageUndoKind(current: PipelineStage): StageUndoKind | null {
  switch (current) {
    case PipelineStage.CITA:
      return 'CANCEL_APPOINTMENT';
    case PipelineStage.PROFORMA:
      return 'ANNUL_QUOTE';
    case PipelineStage.SEPARACION:
    case PipelineStage.VENTA:
      return 'REFUND_DEAL';
    default:
      return null;
  }
}

/** Etapas anteriores a las que "tiene sentido" soltar una tarjeta para retroceder (solo informativo: la etapa real se deriva). */
export function allowedStageBackMoves(current: PipelineStage): PipelineStage[] {
  if (!stageUndoKind(current)) return [];
  const order: PipelineStage[] = [
    PipelineStage.NUEVO,
    PipelineStage.CONTACTADO,
    PipelineStage.CITA,
    PipelineStage.VISITA,
    PipelineStage.PROFORMA,
    PipelineStage.SEPARACION,
    PipelineStage.VENTA,
  ];
  return order.slice(0, order.indexOf(current));
}

export type SubStatusEvent = 'EFFECTIVE_CONTACT' | 'VISIT' | 'SEPARATION' | 'SALE' | 'REFUND';

const PROMOTION_ORDER = [
  'POR_CONTACTAR',
  'NO_GESTIONADO',
  'CONTACTADO',
  'VISITA',
  'SEPARACION',
  'VENTA',
] as const;
const EVENT_TARGET: Record<SubStatusEvent, string> = {
  EFFECTIVE_CONTACT: 'CONTACTADO',
  VISIT: 'VISITA',
  SEPARATION: 'SEPARACION',
  SALE: 'VENTA',
  REFUND: 'DEVOLUCION',
};

/**
 * Promoción automática del sub-estado ante un evento comercial. Solo avanza (nunca retrocede).
 * RECONTACTO avanza como cualquier otro (la unicidad la conserva el flag `isRecontact`);
 * DUPLICADO es una clasificación manual y no se toca. DEVOLUCION se aplica siempre (retroceso deliberado).
 */
export function promoteSubStatus(current: string | null, event: SubStatusEvent): string {
  const target = EVENT_TARGET[event];
  if (event === 'REFUND') return target;
  if (current === 'DUPLICADO') return current;
  if (current === 'RECONTACTO') return target;
  const currentIdx = current
    ? PROMOTION_ORDER.indexOf(current as (typeof PROMOTION_ORDER)[number])
    : -1;
  const targetIdx = PROMOTION_ORDER.indexOf(target as (typeof PROMOTION_ORDER)[number]);
  if (currentIdx === -1 && current && current !== 'DEVOLUCION') return current; // sub-estado personalizado: no tocar
  return targetIdx > currentIdx ? target : (current ?? target);
}
